import DataTable from "@components/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { faTrashCan, faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AdminListCard, ConfirmDeleteModal } from "@components/CatalogIndex";
import type { BatchAction } from "@components/CatalogIndex";
import { useV2DeletePromptOverrideMutation } from "@generated";
import { useAuthorization } from "../../../../common/providers/AuthorizationProvider";
import useListPromptOverrides from "./api";
import { PromptOverride } from "./types";
import { PROMPT_TYPE_OPTIONS } from "../common/types";

const List = (): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const authorized = useAuthorization();
  const [deletePromptOverride, {}] = useV2DeletePromptOverrideMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { promptOverrides, pageMeta, isLoading, refetch } =
    useListPromptOverrides({
      page: {
        number: pageNumber,
        size: pageSize,
      },
    });

  useEffect(() => {
    if ((location.state as any)?.refetch) {
      refetch();
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const handleConfirm = async () => {
    await Promise.all(
      selectedIds.map((id) => deletePromptOverride({ id }).unwrap())
    );
    setSelectedIds([]);
    refetch();
    setShowConfirmationModal(false);
  };

  const getColumns = (): ColumnDef<PromptOverride>[] => {
    if (!authorized || isLoading) {
      return [
        {
          header: "Prompt Type",
          cell() {
            return <FontAwesomeIcon icon={faSpinner} spin />;
          },
        },
        {
          header: "LLM Key",
          cell() {
            return <FontAwesomeIcon icon={faSpinner} spin />;
          },
        },
        {
          header: "Content",
          cell() {
            return <FontAwesomeIcon icon={faSpinner} spin />;
          },
        },
      ];
    }

    if (promptOverrides.length === 0) {
      return [
        {
          header: "Prompt Type",
          id: "empty",
          cell() {
            return <div>No prompt overrides configured</div>;
          },
        },
      ];
    }

    return [
      {
        header: "Prompt Type",
        cell(props) {
          return (
            <div
              className="promptOverrideName"
              onClick={() =>
                navigate(
                  `/admin/prompt_overrides/${props.row.original.id}/edit`
                )
              }
            >
              {PROMPT_TYPE_OPTIONS.find(
                (opt) => opt.value === props.row.original.promptType
              )?.label || props.row.original.promptType}
            </div>
          );
        },
      },
      {
        header: "LLM Key",
        cell(props) {
          return <div>{props.row.original.openRouterKeyName}</div>;
        },
      },
      {
        header: "Content",
        cell(props) {
          const content = props.row.original.content;
          return (
            <div>
              {content.length > 100
                ? `${content.substring(0, 100)}...`
                : content}
            </div>
          );
        },
      },
      {
        header: "Models",
        cell(props) {
          const models = props.row.original.model;
          return <div>{models.length > 0 ? models.join(", ") : ""}</div>;
        },
      },
    ];
  };

  const columns = getColumns();
  const hasData = !!authorized && !isLoading && promptOverrides.length > 0;

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: () => setShowConfirmationModal(true),
      variant: "danger",
    },
  ];

  return (
    <>
      <AdminListCard
        title="Prompt Overrides"
        onNew={() => navigate("/admin/prompt_overrides/new")}
        newButtonLabel="Add a Prompt Override"
        batchActions={batchActions}
        selectedCount={selectedIds.length}
      >
        <DataTable
          data={hasData ? promptOverrides : [{} as PromptOverride]}
          columns={columns}
          selectable={hasData}
          bordered
          striped
          hover
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={setPageNumber}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          paginationEnabled={false}
          pageSizeEnabled={false}
        />
      </AdminListCard>
      <ConfirmDeleteModal
        open={showConfirmationModal}
        title="Confirm Delete"
        message={`Are you sure that you want to delete ${selectedIds.length} prompt override(s)?`}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirmationModal(false)}
      />
    </>
  );
};

export default List;
